<template>
  <Teleport to="body">
    <div
      v-if="isVisible"
      class="fixed inset-0 z-[9999] overflow-hidden overscroll-none flex items-center justify-center p-3 md:p-4"
      @wheel.prevent
    >
    <div
      class="absolute inset-0 bg-black/40 transition-opacity"
      @click="emitClose"
      @touchmove.prevent
    ></div>

    <div
      ref="modalRef"
      class="relative w-full max-w-4xl bg-white rounded-2xl shadow-xl h-[85dvh] md:h-auto md:max-h-[90vh] overflow-hidden flex flex-col overscroll-contain"
      @wheel.stop
      @click.stop
    >
      <!-- Header -->
      <div
        class="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 border-b border-gray-200 flex-shrink-0"
      >
        <h3 class="text-base sm:text-lg font-semibold text-black whitespace-nowrap">
          Couleurs/motifs
        </h3>
        <div class="relative flex-1 max-w-md ml-auto min-w-0">
          <i
            class="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs"
          ></i>
          <input
            ref="searchInputRef"
            v-model.trim="searchTerm"
            type="text"
            autocomplete="off"
            placeholder="Rechercher une couleur"
            class="w-full pl-9 pr-9 py-2 text-sm border border-gray-300 rounded-full focus:outline-none focus:ring-1 focus:ring-black focus:border-black"
            role="combobox"
            aria-label="Rechercher une couleur ou un motif"
            :aria-expanded="isSearchDropdownOpen"
            @focus="openSearchDropdown"
            @blur="closeSearchDropdown"
            @input="openSearchDropdown"
            @keydown.down.prevent="moveColorHighlight(1)"
            @keydown.up.prevent="moveColorHighlight(-1)"
            @keydown.enter.prevent="selectHighlightedColor"
            @keydown.tab.exact.prevent="focusConfirmButton"
          />
          <button
            v-if="searchTerm"
            type="button"
            tabindex="-1"
            class="absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded-full p-1 text-gray-400 hover:text-black"
            aria-label="Effacer la recherche"
            @mousedown.prevent
            @click="clearSearch"
          >
            <i class="fas fa-times text-xs"></i>
          </button>
          <div
            v-if="isSearchDropdownOpen && searchTerm"
            class="absolute left-0 right-0 top-full z-50 mt-1 max-h-64 overflow-y-auto rounded-lg border border-gray-300 bg-white shadow-lg"
            role="listbox"
          >
            <p
              v-if="searchResults.length === 0"
              class="px-3 py-3 text-sm text-gray-500"
            >
              Aucune couleur trouvée
            </p>
            <button
              v-for="(color, index) in searchResults"
              :key="`search-${color.id_couleur}`"
              type="button"
              tabindex="-1"
              :data-color-search-result="color.id_couleur"
              class="flex w-full items-center gap-2 border-b border-gray-100 px-3 py-2 text-left text-sm last:border-b-0"
              :class="
                index === highlightedColorIndex
                  ? 'bg-blue-100'
                  : 'hover:bg-gray-100'
              "
              role="option"
              :aria-selected="index === highlightedColorIndex"
              @mousedown.prevent="selectSearchResult(color)"
            >
              <span
                class="color-swatch-dot h-4 w-4 flex-shrink-0 rounded-full"
                :style="getColorSwatchStyle(color.couleur_FR, color.hexColor)"
              ></span>
              <span class="truncate">{{ color.couleur_FR }}</span>
            </button>
          </div>
        </div>
        <button
          type="button"
          class="text-gray-500 hover:text-black p-2 flex-shrink-0"
          aria-label="Fermer"
          @click="emitClose"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Mobile : navigation horizontale -->
      <div
        class="flex md:hidden flex-col border-b border-gray-100 flex-shrink-0 overscroll-contain"
      >
        <template v-if="visibleColorFamilies.length">
          <button
            type="button"
            class="flex items-center justify-between px-3 py-2 text-sm font-bold text-black uppercase tracking-wide"
            @click="toggleCouleursSection"
          >
            <span>Couleurs</span>
            <i
              class="fas fa-chevron-down collapse-chevron text-[10px] text-gray-400"
              :class="{ 'collapse-chevron--open': couleursSectionOpen }"
            ></i>
          </button>
          <div
            class="collapse-panel"
            :class="couleursSectionOpen ? 'collapse-panel--open' : 'collapse-panel--closed'"
          >
            <div class="collapse-panel__inner">
              <div class="flex gap-2 px-3 pb-2 overflow-x-auto">
            <button
              v-for="family in visibleColorFamilies"
              :key="'mc-' + family.id"
              type="button"
              class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs whitespace-nowrap border flex-shrink-0 transition-colors"
              :class="navButtonClass('couleurs', family.id)"
              @click="selectColorFamily(family.id)"
            >
              <span
                class="color-swatch-dot w-3.5 h-3.5 rounded-full flex-shrink-0"
                :style="familySwatchStyle(family)"
              ></span>
              {{ family.label }}
            </button>
              </div>
            </div>
          </div>
        </template>
        <template v-if="visibleMotifCategories.length">
          <button
            type="button"
            class="flex items-center justify-between px-3 py-2 text-sm font-bold text-black uppercase tracking-wide"
            :class="visibleColorFamilies.length ? 'border-t border-gray-100' : ''"
            @click="toggleMotifsSection"
          >
            <span>Motifs</span>
            <i
              class="fas fa-chevron-down collapse-chevron text-[10px] text-gray-400"
              :class="{ 'collapse-chevron--open': motifsSectionOpen }"
            ></i>
          </button>
          <div
            class="collapse-panel"
            :class="motifsSectionOpen ? 'collapse-panel--open' : 'collapse-panel--closed'"
          >
            <div class="collapse-panel__inner">
              <div class="flex gap-2 px-3 pb-2 overflow-x-auto">
            <button
              v-for="category in visibleMotifCategories"
              :key="'mm-' + category.id"
              type="button"
              class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs whitespace-nowrap border flex-shrink-0 transition-colors"
              :class="navButtonClass('motifs', category.id)"
              @click="selectMotifCategory(category.id)"
            >
              {{ category.label }}
            </button>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- Body -->
      <div class="flex flex-1 min-h-0 overflow-hidden">
        <!-- Desktop : sidebar -->
        <aside
          class="hidden md:block w-44 lg:w-48 flex-shrink-0 bg-gray-50 border-r border-gray-200 overflow-y-auto overscroll-contain"
        >
          <template v-if="visibleColorFamilies.length">
            <button
              type="button"
              class="w-full flex items-center justify-between px-3 py-2.5 text-sm font-bold text-black uppercase tracking-wide hover:bg-white/70 transition-colors"
              @click="toggleCouleursSection"
            >
              <span>Couleurs</span>
              <i
                class="fas fa-chevron-down collapse-chevron text-[10px] text-gray-400"
                :class="{ 'collapse-chevron--open': couleursSectionOpen }"
              ></i>
            </button>
            <div
              class="collapse-panel"
              :class="couleursSectionOpen ? 'collapse-panel--open' : 'collapse-panel--closed'"
            >
              <div class="collapse-panel__inner collapse-panel__inner--sidebar">
              <button
                v-for="family in visibleColorFamilies"
                :key="family.id"
                type="button"
                class="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-left transition-colors"
                :class="sidebarItemClass('couleurs', family.id)"
                @click="selectColorFamily(family.id)"
              >
                <span
                  class="color-swatch-dot w-5 h-5 rounded-full flex-shrink-0"
                  :style="familySwatchStyle(family)"
                ></span>
                <span class="truncate flex-1">{{ family.label }}</span>
                <span
                  v-if="pendingCountByColorFamily[family.id]"
                  class="text-xs text-gray-500"
                >
                  ({{ pendingCountByColorFamily[family.id] }})
                </span>
              </button>
              </div>
            </div>
          </template>

          <template v-if="visibleMotifCategories.length">
            <button
              type="button"
              class="w-full flex items-center justify-between px-3 py-2.5 text-sm font-bold text-black uppercase tracking-wide hover:bg-white/70 transition-colors"
              :class="visibleColorFamilies.length ? 'border-t border-gray-200' : ''"
              @click="toggleMotifsSection"
            >
              <span>Motifs</span>
              <i
                class="fas fa-chevron-down collapse-chevron text-[10px] text-gray-400"
                :class="{ 'collapse-chevron--open': motifsSectionOpen }"
              ></i>
            </button>
            <div
              class="collapse-panel"
              :class="motifsSectionOpen ? 'collapse-panel--open' : 'collapse-panel--closed'"
            >
              <div class="collapse-panel__inner collapse-panel__inner--sidebar">
              <button
                v-for="category in visibleMotifCategories"
                :key="category.id"
                type="button"
                class="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-left transition-colors"
                :class="sidebarItemClass('motifs', category.id)"
                @click="selectMotifCategory(category.id)"
              >
                <span class="truncate flex-1">{{ category.label }}</span>
                <span
                  v-if="pendingCountByMotifCategory[category.id]"
                  class="text-xs text-gray-500"
                >
                  ({{ pendingCountByMotifCategory[category.id] }})
                </span>
              </button>
              </div>
            </div>
          </template>
        </aside>

        <!-- Grille -->
        <section class="flex-1 overflow-y-auto overscroll-contain p-3 sm:p-5">
          <h4
            v-if="panelTitle"
            class="text-sm font-medium text-black mb-3 capitalize"
          >
            {{ panelTitle }}
          </h4>

          <div
            v-if="displayedShades.length === 0"
            class="text-sm text-gray-500 py-8 text-center"
          >
            {{
              searchTerm
                ? "Aucune couleur trouvée"
                : activeSection === "motifs"
                  ? "Aucun motif dans cette famille"
                  : "Aucune couleur dans cette famille"
            }}
          </div>

          <div
            v-else
            class="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-4 sm:gap-x-6 sm:gap-y-5"
          >
            <button
              v-for="color in displayedShades"
              :key="color.id_couleur"
              type="button"
              tabindex="-1"
              class="group text-left focus:outline-none"
              :disabled="isColorDisabled(color.id_couleur)"
              :class="[
                isColorDisabled(color.id_couleur) ? 'opacity-40 cursor-not-allowed' : '',
                isMotif(color)
                  ? isPending(color.id_couleur)
                    ? 'rounded-md border border-black ring-1 ring-black px-2.5 py-2'
                    : 'rounded-md border border-gray-200 px-2.5 py-2 hover:border-gray-400'
                  : '',
              ]"
              @click="toggleColor(color)"
            >
              <div
                class="text-xs sm:text-sm text-black truncate flex items-center justify-between gap-2"
                :class="isMotif(color) ? '' : 'mb-1.5'"
              >
                <span class="truncate">{{ color.couleur_FR }}</span>
                <i
                  v-if="isMotif(color) && isPending(color.id_couleur)"
                  class="fas fa-check text-[10px] flex-shrink-0"
                ></i>
              </div>
              <div
                v-if="!isMotif(color)"
                class="relative w-full h-12 sm:h-16 rounded-md border overflow-hidden transition-all"
                :class="
                  isPending(color.id_couleur)
                    ? 'border-black ring-1 ring-black'
                    : 'border-gray-200 group-hover:border-gray-400'
                "
                :style="getColorSwatchStyle(color.couleur_FR, color.hexColor)"
              >
                <div
                  v-if="isPending(color.id_couleur)"
                  class="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-black text-white flex items-center justify-center shadow"
                >
                  <i class="fas fa-check text-[8px]"></i>
                </div>
              </div>
            </button>
          </div>
        </section>
      </div>

      <!-- Footer -->
      <div
        class="flex items-center justify-end gap-3 px-3 sm:px-4 py-3 border-t border-gray-200 flex-shrink-0"
      >
        <button
          type="button"
          class="px-4 py-2 text-sm border border-gray-800 rounded-lg hover:bg-gray-50 transition-colors"
          @click="emitClose"
        >
          Annuler
        </button>
        <button
          ref="confirmButtonRef"
          type="button"
          class="px-4 py-2 text-sm bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
          @click="confirmAdd"
          @keydown.tab.exact.prevent="focusSearchInput"
        >
          {{ addButtonLabel }}
        </button>
      </div>
    </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onUnmounted, ref, watch } from "vue";
import type { CouleurAPI } from "@/types/mel-components";
import {
  type BasicColorFamily,
  type BasicColorFamilyId,
  getColorSwatchStyle,
  getFamiliesWithVendorColors,
  groupVendorColorsByBasicFamily,
} from "@/utils/colorFamilies";
import {
  type MotifCategory,
  formatMotifPanelTitle,
  getMotifCategoriesWithColors,
  groupMotifsByCategory,
  splitSolidAndMotifColors,
} from "@/utils/motifFamilies";

type PickerSection = "couleurs" | "motifs";

const props = withDefaults(
  defineProps<{
    isVisible: boolean;
    couleursList: CouleurAPI[];
    /** IDs déjà sélectionnés (pré-cochés à l'ouverture) */
    selectedIds?: number[];
    /** Si true (défaut), n’affiche que les couleurs officielles. */
    officialOnly?: boolean;
    /**
     * Couleurs Shopify liées à une couleur officielle (id → libellés source).
     * Affiche « liée : … » sous le nom eFashion.
     */
    linkedSourceLabelsByColorId?: Record<number, string[]>;
    /** Une seule couleur à la fois. */
    single?: boolean;
    /** Couleurs déjà prises, non sélectionnables. */
    disabledIds?: number[];
  }>(),
  {
    officialOnly: true,
  }
);

const emit = defineEmits<{
  "update:isVisible": [value: boolean];
  confirm: [colors: CouleurAPI[]];
}>();

const searchTerm = ref("");
const modalRef = ref<HTMLElement | null>(null);
const searchInputRef = ref<HTMLInputElement | null>(null);
const confirmButtonRef = ref<HTMLButtonElement | null>(null);
const highlightedColorIndex = ref(-1);
const isSearchDropdownOpen = ref(false);
const activeSection = ref<PickerSection>("couleurs");
const activeFamilyId = ref<BasicColorFamilyId>("beige");
const activeMotifCategory = ref<MotifCategory>("Animaux");
const pendingIds = ref<Set<number>>(new Set());
const couleursSectionOpen = ref(true);
const motifsSectionOpen = ref(false);

const familySwatchStyle = (family: BasicColorFamily) => {
  if (family.swatch.startsWith("linear-gradient")) {
    return { backgroundImage: family.swatch };
  }
  return { backgroundColor: family.swatch };
};

const disableBodyScroll = () => {
  document.documentElement.style.overflow = "hidden";
  document.body.style.overflow = "hidden";
};

const enableBodyScroll = () => {
  document.documentElement.style.overflow = "";
  document.body.style.overflow = "";
};

const normalize = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const matchesSearch = (color: CouleurAPI, term: string) =>
  normalize(color.couleur_FR || "").includes(term) ||
  normalize(color.couleur_EN || "").includes(term) ||
  linkedSourceLabels(color.id_couleur).some((label) =>
    normalize(label).includes(term),
  );

const linkedSourceLabels = (colorId: number | string) => {
  const id = Number(colorId);
  if (!Number.isInteger(id) || id <= 0) return [];
  return props.linkedSourceLabelsByColorId?.[id] ?? [];
};

const availableColors = computed(() => {
  const list = props.couleursList || [];
  if (!props.officialOnly) return list;
  const hasOfficielFlag = list.some(
    (c) => c.officiel !== undefined && c.officiel !== null,
  );
  if (!hasOfficielFlag) return list;
  return list.filter((c) => Number(c.officiel) === 1);
});

const solidColorsRef = computed(() =>
  splitSolidAndMotifColors(availableColors.value).solid,
);
const motifColorsRef = computed(() =>
  splitSolidAndMotifColors(availableColors.value).motifs,
);

const colorGroups = computed(() =>
  groupVendorColorsByBasicFamily(solidColorsRef.value),
);

const motifGroups = computed(() => groupMotifsByCategory(motifColorsRef.value));

const visibleColorFamilies = computed(() => {
  if (searchTerm.value) {
    const term = normalize(searchTerm.value);
    return getFamiliesWithVendorColors(solidColorsRef.value).filter((family) =>
      (colorGroups.value[family.id] || []).some((color) =>
        matchesSearch(color, term),
      ),
    );
  }
  return getFamiliesWithVendorColors(solidColorsRef.value);
});

const visibleMotifCategories = computed(() => {
  if (searchTerm.value) {
    const term = normalize(searchTerm.value);
    return getMotifCategoriesWithColors(motifColorsRef.value).filter(
      (category) =>
        (motifGroups.value[category.id] || []).some((color) =>
          matchesSearch(color, term),
        ),
    );
  }
  return getMotifCategoriesWithColors(motifColorsRef.value);
});

const displayedShades = computed(() => {
  const term = searchTerm.value ? normalize(searchTerm.value) : "";

  if (activeSection.value === "motifs") {
    let list = motifGroups.value[activeMotifCategory.value] || [];
    if (term) list = list.filter((color) => matchesSearch(color, term));
    return list;
  }

  let list = colorGroups.value[activeFamilyId.value] || [];
  if (term) list = list.filter((color) => matchesSearch(color, term));
  return list;
});

const searchResults = computed(() => {
  const term = normalize(searchTerm.value);
  if (!term) return [];
  return availableColors.value.filter(
    (color) =>
      !isPending(color.id_couleur) && matchesSearch(color, term),
  );
});

const panelTitle = computed(() => {
  if (activeSection.value === "motifs") {
    return formatMotifPanelTitle(activeMotifCategory.value);
  }
  const family = visibleColorFamilies.value.find(
    (item) => item.id === activeFamilyId.value,
  );
  return family ? `Nuances de ${family.label.toLowerCase()}` : "";
});

const pendingCountByColorFamily = computed(() => {
  const counts: Partial<Record<BasicColorFamilyId, number>> = {};
  for (const family of visibleColorFamilies.value) {
    const count = (colorGroups.value[family.id] || []).filter((color) =>
      pendingIds.value.has(parseInt(String(color.id_couleur), 10)),
    ).length;
    if (count > 0) counts[family.id] = count;
  }
  return counts;
});

const pendingCountByMotifCategory = computed(() => {
  const counts: Partial<Record<MotifCategory, number>> = {};
  for (const category of visibleMotifCategories.value) {
    const count = (motifGroups.value[category.id] || []).filter((color) =>
      pendingIds.value.has(parseInt(String(color.id_couleur), 10)),
    ).length;
    if (count > 0) counts[category.id] = count;
  }
  return counts;
});

const addButtonLabel = computed(() => {
  const n = pendingIds.value.size;
  if (n === 0) return "Valider";
  if (n === 1) return "Ajouter 1 couleur";
  return `Ajouter ${n} couleurs`;
});

const isPending = (id: number | string) =>
  pendingIds.value.has(parseInt(String(id), 10));

const isMotif = (color: CouleurAPI) => Boolean(color.motifCategorie);

const scrollHighlightedColorIntoView = () => {
  const color = searchResults.value[highlightedColorIndex.value];
  if (!color) return;
  const element = modalRef.value?.querySelector<HTMLElement>(
    `[data-color-search-result="${color.id_couleur}"]`,
  );
  element?.scrollIntoView({ block: "nearest" });
};

const moveColorHighlight = (delta: number) => {
  isSearchDropdownOpen.value = true;
  const length = searchResults.value.length;
  if (!length) return;
  if (highlightedColorIndex.value < 0) {
    highlightedColorIndex.value = delta > 0 ? 0 : length - 1;
  } else {
    highlightedColorIndex.value =
      (highlightedColorIndex.value + delta + length) % length;
  }
  void nextTick(scrollHighlightedColorIntoView);
};

const selectHighlightedColor = () => {
  const color =
    searchResults.value[
      highlightedColorIndex.value >= 0 ? highlightedColorIndex.value : 0
    ];
  if (color) selectSearchResult(color);
};

const showColorPage = (color: CouleurAPI) => {
  const colorId = Number(color.id_couleur);
  if (isMotif(color)) {
    const category = visibleMotifCategories.value.find((item) =>
      (motifGroups.value[item.id] || []).some(
        (candidate) => Number(candidate.id_couleur) === colorId,
      ),
    );
    if (category) selectMotifCategory(category.id);
    return;
  }

  const family = visibleColorFamilies.value.find((item) =>
    (colorGroups.value[item.id] || []).some(
      (candidate) => Number(candidate.id_couleur) === colorId,
    ),
  );
  if (family) selectColorFamily(family.id);
};

const selectSearchResult = (color: CouleurAPI) => {
  showColorPage(color);
  toggleColor(color);
  searchTerm.value = "";
  isSearchDropdownOpen.value = false;
  highlightedColorIndex.value = -1;
  void nextTick(() => searchInputRef.value?.focus());
};

const clearSearch = () => {
  searchTerm.value = "";
  isSearchDropdownOpen.value = false;
  highlightedColorIndex.value = -1;
  void nextTick(() => searchInputRef.value?.focus());
};

const openSearchDropdown = (event?: Event) => {
  const fieldValue =
    event?.target instanceof HTMLInputElement
      ? event.target.value
      : searchTerm.value;
  isSearchDropdownOpen.value = Boolean(fieldValue.trim());
};

const closeSearchDropdown = () => {
  isSearchDropdownOpen.value = false;
};

const focusConfirmButton = () => {
  closeSearchDropdown();
  confirmButtonRef.value?.focus();
};

const focusSearchInput = () => {
  searchInputRef.value?.focus();
};

const isActiveNav = (section: PickerSection, id: string) =>
  activeSection.value === section &&
  (section === "couleurs"
    ? activeFamilyId.value === id
    : activeMotifCategory.value === id);

const navButtonClass = (section: PickerSection, id: string) =>
  isActiveNav(section, id)
    ? "bg-black text-white border-black"
    : "bg-white text-black border-gray-300";

const sidebarItemClass = (section: PickerSection, id: string) =>
  isActiveNav(section, id)
    ? "bg-white text-black font-medium shadow-sm"
    : "text-gray-700 hover:bg-white/70";

const selectColorFamily = (familyId: BasicColorFamilyId) => {
  activeSection.value = "couleurs";
  activeFamilyId.value = familyId;
  couleursSectionOpen.value = true;
  motifsSectionOpen.value = false;
};

const selectMotifCategory = (category: MotifCategory) => {
  activeSection.value = "motifs";
  activeMotifCategory.value = category;
  motifsSectionOpen.value = true;
  couleursSectionOpen.value = false;
};

const toggleCouleursSection = () => {
  const willOpen = !couleursSectionOpen.value;
  couleursSectionOpen.value = willOpen;
  if (willOpen) motifsSectionOpen.value = false;
};

const toggleMotifsSection = () => {
  const willOpen = !motifsSectionOpen.value;
  motifsSectionOpen.value = willOpen;
  if (willOpen) couleursSectionOpen.value = false;
};

const isColorDisabled = (colorId: number | string) => {
  const id = parseInt(String(colorId), 10);
  return (props.disabledIds ?? []).some((item) => Number(item) === id);
};

const toggleColor = (color: CouleurAPI) => {
  const id = parseInt(String(color.id_couleur), 10);
  if (isColorDisabled(id)) return;
  if (props.single) {
    pendingIds.value = pendingIds.value.has(id) ? new Set() : new Set([id]);
    return;
  }
  const next = new Set(pendingIds.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  pendingIds.value = next;
};

const emitClose = () => {
  emit("update:isVisible", false);
};

const confirmAdd = () => {
  const selected = availableColors.value.filter((color) =>
    pendingIds.value.has(parseInt(String(color.id_couleur), 10)),
  );
  emit("confirm", selected);
  emit("update:isVisible", false);
};

const resetState = () => {
  searchTerm.value = "";
  const initial = new Set<number>();
  for (const id of props.selectedIds || []) {
    initial.add(parseInt(String(id), 10));
  }
  pendingIds.value = initial;

  const colorFamilies = getFamiliesWithVendorColors(solidColorsRef.value);
  const motifCategories = getMotifCategoriesWithColors(motifColorsRef.value);

  if (colorFamilies.length) {
    activeSection.value = "couleurs";
    activeFamilyId.value = colorFamilies[0]?.id || "autres";
    couleursSectionOpen.value = true;
    motifsSectionOpen.value = false;
  } else if (motifCategories.length) {
    activeSection.value = "motifs";
    activeMotifCategory.value = motifCategories[0].id;
    couleursSectionOpen.value = false;
    motifsSectionOpen.value = true;
  }
};

watch(
  () => props.isVisible,
  (visible) => {
    if (visible) {
      resetState();
      nextTick(() => {
        disableBodyScroll();
        searchInputRef.value?.focus();
      });
    } else {
      enableBodyScroll();
    }
  },
  { immediate: true },
);

onUnmounted(() => {
  enableBodyScroll();
});

watch([visibleColorFamilies, visibleMotifCategories], ([families, categories]) => {
  if (
    activeSection.value === "couleurs" &&
    !families.find((family) => family.id === activeFamilyId.value)
  ) {
    if (families[0]) {
      activeFamilyId.value = families[0].id;
    } else if (categories[0]) {
      activeSection.value = "motifs";
      activeMotifCategory.value = categories[0].id;
      motifsSectionOpen.value = true;
      couleursSectionOpen.value = false;
    }
  }

  if (
    activeSection.value === "motifs" &&
    !categories.find((category) => category.id === activeMotifCategory.value)
  ) {
    if (categories[0]) {
      activeMotifCategory.value = categories[0].id;
    } else if (families[0]) {
      activeSection.value = "couleurs";
      activeFamilyId.value = families[0].id;
      couleursSectionOpen.value = true;
      motifsSectionOpen.value = false;
    }
  }
});

watch(searchTerm, () => {
  highlightedColorIndex.value = -1;
  if (!searchTerm.value) return;

  if (visibleColorFamilies.value[0]) {
    activeSection.value = "couleurs";
    activeFamilyId.value = visibleColorFamilies.value[0].id;
    couleursSectionOpen.value = true;
    motifsSectionOpen.value = false;
    return;
  }

  if (visibleMotifCategories.value[0]) {
    activeSection.value = "motifs";
    activeMotifCategory.value = visibleMotifCategories.value[0].id;
    motifsSectionOpen.value = true;
    couleursSectionOpen.value = false;
  }
});
</script>

<style scoped>
.collapse-panel {
  display: grid;
  transition:
    grid-template-rows 0.28s ease,
    opacity 0.22s ease;
}

.collapse-panel--open {
  grid-template-rows: 1fr;
  opacity: 1;
}

.collapse-panel--closed {
  grid-template-rows: 0fr;
  opacity: 0.72;
}

.collapse-panel__inner {
  overflow: hidden;
  min-height: 0;
}

.collapse-panel__inner--sidebar > button {
  animation: collapse-item-in 0.24s ease both;
}

.collapse-panel--open .collapse-panel__inner--sidebar > button:nth-child(1) {
  animation-delay: 0.02s;
}
.collapse-panel--open .collapse-panel__inner--sidebar > button:nth-child(2) {
  animation-delay: 0.04s;
}
.collapse-panel--open .collapse-panel__inner--sidebar > button:nth-child(3) {
  animation-delay: 0.06s;
}
.collapse-panel--open .collapse-panel__inner--sidebar > button:nth-child(4) {
  animation-delay: 0.08s;
}
.collapse-panel--open .collapse-panel__inner--sidebar > button:nth-child(n + 5) {
  animation-delay: 0.1s;
}

.collapse-chevron {
  transition: transform 0.28s ease;
}

.collapse-chevron--open {
  transform: rotate(180deg);
}

@keyframes collapse-item-in {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.color-swatch-dot {
  display: inline-block;
  border-radius: 50%;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.18);
  background-repeat: no-repeat;
  background-size: 100% 100%;
  background-clip: padding-box;
}
</style>
